// Reines Mathe-Modul für das releasable Magnet-Snapping der Ansichtsfenster
// im Drucken-Modal (print_plan_3d_view.js) — bewusst ohne jeden Import:
// print_plan_render.js sieht zwar nach "nur Konstanten" aus, zieht über
// captureLivePlanSnapshot() aber auch den kompletten 2D-Renderer (plan_renderer.js)
// mit rein — und darüber transitiv Module mit DOM-Seiteneffekten beim reinen
// Import (z.B. sketch_wall_interaction.js registriert beim Laden
// unconditional einen document-Listener). print_plan_3d_view.js ist über
// project_state_service.js Teil des in scripts/check-dimension-service.mjs
// (Node, kein DOM) geprüften Import-Graphen — ein Import von
// print_plan_render.js hier würde also genau diesen Check zum Absturz
// bringen. Die Titelkopf-Geometrie kommt deshalb als reine Daten von aussen
// (print_plan_modal.js kennt die Konstanten bereits), nicht als Import.
//
// Anders als die bestehenden "sticky" Snap-Systeme (H-3000-Wände,
// Multischrank, Flexiport-Base — die beim Loslassen dauerhaft verschweissen
// und nur über eine separate Lösen-Aktion wieder getrennt werden), wird hier
// NICHTS persistiert: updateAxisSnap wird bei jedem pointermove neu
// aufgerufen und liefert nur eine für den aktuellen Tick gültige Korrektur.
//
// Alle Linien/Fenster-Ränder verwenden CSS box-sizing:border-box — die
// sichtbare Strichlinie wird IMMER von der geometrischen Boxkante nach innen
// gezeichnet, nie exakt AUF ihr. Referenzen werden deshalb als sichtbare
// Linienbänder {start, end} geführt. Beim Snap wird die gemeinsame Innenkante
// der beiden Bänder ausgerichtet: bei einer linken/oberen Fensterkante deren
// Ende, bei einer rechten/unteren Kante deren Anfang. Das ist insbesondere
// dann wichtig, wenn Fensterrahmen und Zeichnungskopf unterschiedliche
// Linienstärken haben. Eine Ausrichtung der Mittellinien würde den dickeren
// Rahmen um die halbe Differenz der Linienstärken seitlich versetzen.
export const SNAP_ENGAGE_MM = 5;
export const SNAP_RELEASE_MM = 7;

// frameMm: {width, height, borderMm} — borderMm ist die Rahmenlinien-Dicke
// (border_thickness_mm), die auch für Fensterrahmen gilt (beide nutzen die
// gleiche CSS-Variable --print-frame-border-w).
// titleBlock: null, wenn aktuell "Ohne" gewählt ist (dann ist dort nichts
// gerendert und es darf auch nichts eingerastet werden), sonst {widthMm,
// heightMm, borderMm, overlapMm,
// sectionWidthsMm: {logo, betrifft, produktinformation}}.
// windows: bereits gefilterte Liste der ANDEREN Fenster (nie das gezogene/
// skalierte selbst) — {xMm, zMm, widthMm, heightMm}.
//
// #printPlanWindowsLayer (wo win.xMm/zMm=0 beginnt) füllt exakt die PADDING-
// Box von #printPlanFrame, also innerhalb von dessen eigener Rahmenlinie,
// nicht bis zu deren Aussenkante — die nutzbare Breite/Höhe ist deshalb
// frameMm.width/height MINUS 2x Rahmenlinie, nicht frameMm.width/height
// selbst (sonst würde ein an "frameMm.width" gesnapptes Fenster über den
// sichtbaren, von #printPlanWindowsLayer weggeclippten Bereich hinausragen).
export function collectReferenceLines(frameMm, titleBlock, windows) {
  const frameBorderMm = frameMm.borderMm || 0;
  const innerWidthMm = frameMm.width - 2 * frameBorderMm;
  const innerHeightMm = frameMm.height - 2 * frameBorderMm;

  const vertical = [
    { start: -frameBorderMm, end: 0 },
    { start: innerWidthMm, end: innerWidthMm + frameBorderMm }
  ];
  const horizontal = [
    { start: -frameBorderMm, end: 0 },
    { start: innerHeightMm, end: innerHeightMm + frameBorderMm }
  ];

  if (titleBlock) {
    const tbBorderMm = titleBlock.borderMm || 0;
    const overlapMm = titleBlock.overlapMm || 0;
    // Die bewusst kleine Überdeckung der rechten/unteren Rahmenkante gehört
    // zur echten Geometrie und wird in Vorschau und Druck identisch skaliert.
    const tbRight = innerWidthMm + frameBorderMm + overlapMm;
    const tbBottom = innerHeightMm + frameBorderMm + overlapMm;
    const tbLeft = tbRight - titleBlock.widthMm;
    const tbTop = tbBottom - titleBlock.heightMm;
    const w = titleBlock.sectionWidthsMm;
    // Die 4 Feldbreiten sind CSS flex-basis-Prozentsätze (33.334%/30%/
    // 22.223%/14.443%), die sich auf die INHALTSBOX des Zeichnungskopfs
    // beziehen — bei box-sizing:border-box ohne Padding ist die Inhaltsbox
    // um die eigene Randlinie (tbBorderMm, auf beiden Seiten) schmäler als
    // widthMm selbst. Ohne diese Skalierung würden die kumulierten
    // Trennlinien-Positionen mit jedem weiteren Feld zunehmend von den
    // tatsächlich gerenderten abweichen (bei Feld 3 bereits >1px sichtbar).
    const contentScale = (titleBlock.widthMm - 2 * tbBorderMm) / titleBlock.widthMm;
    const contentLeft = tbLeft + tbBorderMm;
    const logoW = w.logo * contentScale;
    const betrifftW = w.betrifft * contentScale;
    const produktinfoW = w.produktinformation * contentScale;
    const logoRight = contentLeft + logoW;
    const betrifftRight = logoRight + betrifftW;
    const produktinfoRight = betrifftRight + produktinfoW;
    // tbLeft: border-left wird ab der Aussenkante nach rechts gezeichnet.
    // Interne Trennlinien sind border-right und enden an der jeweiligen
    // Spaltenkante. Das vollständige Linienband bleibt erhalten, damit der
    // Snap auch bei abweichender Fenster-Linienstärke die sichtbare Kante
    // statt einer rechnerischen Mittellinie ausrichten kann.
    vertical.push(
      { start: tbLeft, end: tbLeft + tbBorderMm },
      { start: logoRight - tbBorderMm, end: logoRight },
      { start: betrifftRight - tbBorderMm, end: betrifftRight },
      { start: produktinfoRight - tbBorderMm, end: produktinfoRight }
    );
    horizontal.push({ start: tbTop, end: tbTop + tbBorderMm });
    // tbRight/tbBottom fallen bis auf die bewusst modellierte Überdeckung
    // mit den Rahmenkanten zusammen und werden nicht doppelt aufgenommen.
  }

  windows.forEach(w => {
    vertical.push(
      { start: w.xMm, end: w.xMm + frameBorderMm },
      { start: w.xMm + w.widthMm - frameBorderMm, end: w.xMm + w.widthMm }
    );
    horizontal.push(
      { start: w.zMm, end: w.zMm + frameBorderMm },
      { start: w.zMm + w.heightMm - frameBorderMm, end: w.zMm + w.heightMm }
    );
  });

  return { vertical, horizontal };
}

// prevState: {edgeOffset, line, edgeValue} | null.
// edgeCandidates: [{offset, value}] — 1 Eintrag beim Resize (nur eine Kante
// pro Achse bewegt sich), 2 beim Move (führende Kante offset=0, folgende
// Kante offset=1, starr gekoppelt, da eine Translation beide gleichzeitig
// verschiebt). lines: Array<{start, end}> in mm mit den vollständigen
// sichtbaren Linienbändern (siehe collectReferenceLines). ownBorderMm:
// Strichstärke der GEZOGENEN Fensterkante selbst. offset=0 ist eine führende
// (links/oben), offset=1
// eine nachlaufende Kante (rechts/unten). Bei offset=0 wird das ENDE des
// eigenen Linienbands auf das Ende der Referenz gelegt; bei offset=1 dessen
// ANFANG auf den Anfang der Referenz. So bleibt die sichtbare Kante auch bei
// verschiedenen Linienstärken exakt durchgehend.
//
// Hysterese: solange die zuvor eingerastete Linie noch innerhalb von
// SNAP_RELEASE_MM liegt, bleibt sie aktiv (verhindert Flackern genau an der
// 5mm-Schwelle); erst danach wird neu gesucht (Einrasten ab SNAP_ENGAGE_MM).
export function updateAxisSnap(prevState, edgeCandidates, lines, ownBorderMm = 0) {
  if (prevState) {
    const edge = edgeCandidates.find(e => e.offset === prevState.edgeOffset);
    if (edge && Math.abs(edge.value - prevState.line) <= SNAP_RELEASE_MM) {
      return { edgeOffset: edge.offset, line: prevState.line, guideLine: prevState.guideLine, edgeValue: edge.value };
    }
  }
  let best = null;
  for (const edge of edgeCandidates) {
    for (const band of lines) {
      const target = edge.offset === 1
        ? band.start + ownBorderMm
        : band.end - ownBorderMm;
      const guideLine = edge.offset === 1 ? band.start : band.end;
      const distance = Math.abs(edge.value - target);
      if (distance > SNAP_ENGAGE_MM) continue;
      if (!best || distance < best.distance) {
        best = { distance, edgeOffset: edge.offset, line: target, guideLine, edgeValue: edge.value };
      }
    }
  }
  return best
    ? { edgeOffset: best.edgeOffset, line: best.line, guideLine: best.guideLine, edgeValue: best.edgeValue }
    : null;
}

// Andocken eines 2D-Fensters an eine der 4 Kanten der Projekt-Bounding-Box
// (Rahmen-mm, siehe frameWorldOrigin-Beziehung in print_plan_modal.js) — anders
// als updateAxisSnap sperrt dies nur EINE Achse (senkrecht zur Andock-Kante),
// die parallele Achse bleibt frei für die normale Verschiebung/den allgemeinen
// Fenster-Snap.
//
// prevState: {edge:'top'|'bottom'|'left'|'right'} | null.
// windowRectMm: {xMm, zMm, widthMm, heightMm} — die AKTUELLE (ungesperrte)
// Fensterposition/-grösse in diesem Tick.
// boundsMm: {left, top, right, bottom} — Projekt-Bounding-Box in Rahmen-mm.
//
// Rückgabe: null (nicht angedockt) oder {edge, xMm, zMm} — xMm/zMm ist jeweils
// NUR für die gesperrte Achse gesetzt (die andere ist null, "nicht von
// dockToBounds bestimmt"). xMm/zMm werden hier JEDEN Aufruf frisch aus der
// aktuellen widthMm/heightMm berechnet (nie als fixer Wert eingefroren) —
// bleibt dadurch auch während eines Resize-Griffs korrekt, siehe Kommentar im
// Plan zu diesem Modul.
// Reine Kantenformel, aus dockToBounds() extrahiert, damit sowohl das Andocken
// beim Ziehen als auch das laufende Re-Pinning eines bereits angedockten
// Fensters bei Massstabsänderungen (print_plan_2d_views.js's
// syncViewWindowsGeometry()) dieselbe Formel nutzen. sizeMm: {widthMm,
// heightMm} der AKTUELLEN (ggf. gerade neu berechneten) Fenstergrösse — nie
// als fixer Wert eingefroren. Rückgabe: {xMm, zMm}, je eine Achse null (die
// zur Kante senkrechte).
export function pinnedPositionForEdge(edge, sizeMm, boundsMm) {
  const xMm = edge === 'left' ? boundsMm.left - sizeMm.widthMm
    : edge === 'right' ? boundsMm.right
    : null;
  const zMm = edge === 'top' ? boundsMm.top - sizeMm.heightMm
    : edge === 'bottom' ? boundsMm.bottom
    : null;
  return { xMm, zMm };
}

// Kreuz-Ausrichtung der 2D-Fenster (Sidebar-3x3-Raster "_ N _ / W Top O / _ S
// _"): die Spalte {Nord, Draufsicht, Süd} teilt sich eine horizontale Position
// (xMm), die Zeile {West, Draufsicht, Ost} eine vertikale (zMm) — jeweils die
// ANDERE Achse bleibt pro Fenster unabhängig. "top" ist Mitglied beider
// Gruppen (Kreuzmitte).
export const COLUMN_LOCK_DIRECTIONS = ['north', 'top', 'south'];
export const ROW_LOCK_DIRECTIONS = ['west', 'top', 'east'];

// direction (optional): schränkt ein, welche Kanten überhaupt Andock-
// Kandidaten sein dürfen — die geteilte Kreuz-Achse eines Fensters (xMm bei
// COLUMN_LOCK_DIRECTIONS, zMm bei ROW_LOCK_DIRECTIONS) darf NIE selbst
// andocken. Grund: ein per Kreuz-Ausrichtung erzeugtes Fenster hat diese Achse
// per Konstruktion bereits exakt auf den entsprechenden Bounds-Kanten
// (Nord/Süd erben xMm von der Draufsicht, deren xMm/widthMm selbst exakt
// boundsMm.left/right entspricht) — ohne diese Einschränkung würde JEDE
// unabhängige Bewegung auf der jeweils ANDEREN (eigentlich freien) Achse die
// noch bei Distanz 0 stehende, gemeinsame Achse fälschlich als "gerade
// angedockt" erkennen (deren Distanz bleibt ja unverändert 0, während die
// tatsächlich bewegte Achse von 0 wegwächst und den Kantenvergleich verliert)
// — das liess Fenster beim Ziehen quer durchs Bild springen. `direction`
// fehlt/undefined (z.B. in bestehenden Tests) erlaubt weiterhin alle 4 Kanten.
function allowedDockAxes(direction) {
  return {
    allowX: !COLUMN_LOCK_DIRECTIONS.includes(direction),
    allowZ: !ROW_LOCK_DIRECTIONS.includes(direction)
  };
}

// BUGFIX (Fenster sprangen teils um fast die eigene Breite/Höhe beim
// Verschieben): `raw`/`target` müssen dieselbe Koordinate vergleichen, die
// pinnedPositionForEdge() tatsächlich setzt (xMm für links/rechts, zMm für
// oben/unten) — NICHT die jeweils NAHE Kante gegen die Ziel-Blattkante.
// Vorher verglich z.B. 'right' die eigene RECHTE Kante (xMm+widthMm) mit
// boundsMm.right, während das Andocken selbst die LINKE Kante (xMm) exakt
// auf boundsMm.right setzt (pinnedPositionForEdge('right',...) => xMm:
// boundsMm.right) — ein Fenster, dessen rechte Kante zufällig nahe an
// boundsMm.right lag, aber dessen linke Kante noch weit entfernt war, löste
// dadurch einen Sprung von fast der ganzen Fensterbreite aus, sobald man
// "knapp an der Draufsicht vorbei" zog. Jetzt wird für jede Kante direkt die
// Zielkoordinate aus pinnedPositionForEdge() als target verwendet, wodurch
// ein Andocken nur noch auslöst, wenn die tatsächliche ZIELPOSITION bereits
// innerhalb der Snap-Schwelle liegt — der Sprung ist dadurch nie grösser als
// SNAP_ENGAGE_MM.
export function dockToBounds(prevState, windowRectMm, boundsMm, direction) {
  const { allowX, allowZ } = allowedDockAxes(direction);
  const topTarget = pinnedPositionForEdge('top', windowRectMm, boundsMm).zMm;
  const bottomTarget = pinnedPositionForEdge('bottom', windowRectMm, boundsMm).zMm;
  const leftTarget = pinnedPositionForEdge('left', windowRectMm, boundsMm).xMm;
  const rightTarget = pinnedPositionForEdge('right', windowRectMm, boundsMm).xMm;
  const candidates = [
    ...(allowZ ? [
      { edge: 'top', raw: windowRectMm.zMm, target: topTarget },
      { edge: 'bottom', raw: windowRectMm.zMm, target: bottomTarget }
    ] : []),
    ...(allowX ? [
      { edge: 'left', raw: windowRectMm.xMm, target: leftTarget },
      { edge: 'right', raw: windowRectMm.xMm, target: rightTarget }
    ] : [])
  ];
  if (!candidates.length) return null;

  let matchedEdge = null;
  if (prevState) {
    const current = candidates.find(c => c.edge === prevState.edge);
    if (current && Math.abs(current.raw - current.target) <= SNAP_RELEASE_MM) {
      matchedEdge = prevState.edge;
    }
  }
  if (!matchedEdge) {
    let best = null;
    for (const c of candidates) {
      const distance = Math.abs(c.raw - c.target);
      if (distance > SNAP_ENGAGE_MM) continue;
      if (!best || distance < best.distance) best = { ...c, distance };
    }
    matchedEdge = best ? best.edge : null;
  }
  if (!matchedEdge) return null;

  const { xMm, zMm } = pinnedPositionForEdge(matchedEdge, windowRectMm, boundsMm);
  return { edge: matchedEdge, xMm, zMm };
}

// oben→Nord, unten→Süd, links→West, rechts→Ost (gleiche Konvention wie
// print_plan_2d_views.js: Norden = -Z-Weltachse).
export const DOCK_EDGE_DIRECTIONS = { top: 'north', bottom: 'south', left: 'west', right: 'east' };

// Auf welcher Seite der Draufsicht (boundsMm) liegt ein frei verschobenes
// Fenster gerade — für die automatische Blickrichtungs-Umschaltung beim
// Loslassen (reassignDirectionForPosition() in print_plan_2d_views.js),
// damit eine Verschiebung auf die "falsche" Seite nicht gesperrt werden muss,
// sondern stattdessen die passende Projektion erzeugt wird. Die Seite mit dem
// GRÖSSTEN Überstand über boundsMm hinaus gewinnt, damit ein Fenster nahe der
// Mitte (kein Überstand auf irgendeiner Seite) stabil bleibt, statt bei
// kleinen Zitterbewegungen zwischen zwei Richtungen zu kippen. Rückgabe:
// 'north'|'south'|'west'|'east' oder null (Zentrum liegt über/in boundsMm).
export function classifyDraufsichtSide(windowRectMm, boundsMm) {
  const centerX = windowRectMm.xMm + windowRectMm.widthMm / 2;
  const centerZ = windowRectMm.zMm + windowRectMm.heightMm / 2;
  const candidates = [
    { side: 'north', amount: boundsMm.top - centerZ },
    { side: 'south', amount: centerZ - boundsMm.bottom },
    { side: 'west', amount: boundsMm.left - centerX },
    { side: 'east', amount: centerX - boundsMm.right }
  ].filter(c => c.amount > 0);
  if (!candidates.length) return null;
  candidates.sort((a, b) => b.amount - a.amount);
  return candidates[0].side;
}

// snapshot: [{id, direction, xMm, zMm}] — Zustand ALLER Fenster VOR der
// Änderung (bei einer Ziehgeste: deren Start; beim laufenden Andock-Re-Pin:
// der aktuelle Tick vor dem Re-Pin). changedId/newXMm/newZMm: das eine
// Fenster, dessen Position sich gerade geändert hat, mit seinem NEUEN Wert
// (nie ein pro-Tick-Delta, um Drift über viele kleine Schritte zu vermeiden —
// dx/dz werden hier immer relativ zum ursprünglichen Snapshot-Eintrag
// berechnet). Gibt Patches NUR für die jeweils ANDEREN Fenster derselben
// Lock-Gruppe zurück (nie für changedId selbst, das hat der Aufrufer bereits).
export function propagateGroupDrag(snapshot, changedId, newXMm, newZMm) {
  const changed = snapshot.find(entry => entry.id === changedId);
  if (!changed) return [];
  const dxMm = newXMm - changed.xMm;
  const dzMm = newZMm - changed.zMm;
  const patches = new Map();
  const patch = (id, key, value) => {
    const entry = patches.get(id) || { id };
    entry[key] = value;
    patches.set(id, entry);
  };

  if (dxMm !== 0 && COLUMN_LOCK_DIRECTIONS.includes(changed.direction)) {
    snapshot.forEach(entry => {
      if (entry.id === changedId) return;
      if (!COLUMN_LOCK_DIRECTIONS.includes(entry.direction)) return;
      patch(entry.id, 'xMm', entry.xMm + dxMm);
    });
  }
  if (dzMm !== 0 && ROW_LOCK_DIRECTIONS.includes(changed.direction)) {
    snapshot.forEach(entry => {
      if (entry.id === changedId) return;
      if (!ROW_LOCK_DIRECTIONS.includes(entry.direction)) return;
      patch(entry.id, 'zMm', entry.zMm + dzMm);
    });
  }
  return Array.from(patches.values());
}
